import React, { useState } from 'react';
import { Badge, Alert, TableSkeleton, Button, Modal } from '@/components/ui';
import type {
  User,
  Organization,
  UpdateUserRequest,
} from '@/services/admin/AdminService';
import { ALL_USER_ROLES } from '@/constants/userRoles';

// Pencil edit icon
const PencilIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className || 'w-4 h-4'}
  >
    <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
  </svg>
);

interface UsersTableProps {
  users: User[];
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  actionLoading: boolean;
  onUpdateUser: (uid: string, data: UpdateUserRequest) => Promise<User | null>;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  organizations,
  loading,
  error,
  actionLoading,
  onUpdateUser,
}) => {
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserRequest>({});

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditForm({
      systemRole: user.systemRole,
      organization_id: user.organization_id,
      is_active: user.is_active !== false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    const result = await onUpdateUser(editingUser.uid, editForm);
    if (result) {
      setEditingUser(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const getOrganizationName = (orgId?: string) => {
    if (!orgId) return null;
    const org = organizations.find((o) => o.id === orgId);
    return org?.name || orgId;
  };

  if (loading) {
    return <TableSkeleton rows={6} columns={7} />;
  }

  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }

  if (users.length === 0) {
    return <Alert variant="info">No users found in the system.</Alert>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-secondary-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Email
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Display Name
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                System Role
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Organization
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Status
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Last Login
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr
                key={user.uid}
                className="border-b border-secondary-100 hover:bg-secondary-50"
              >
                <td className="py-3 px-4">
                  <span className="font-semibold text-secondary-900">
                    {user.email}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-secondary-900">
                    {user.display_name || 'N/A'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  {user.systemRole ? (
                    <Badge
                      variant={
                        user.systemRole === 'ADMIN' ? 'primary' : 'default'
                      }
                    >
                      {user.systemRole}
                    </Badge>
                  ) : (
                    <span className="text-sm text-secondary-500">No Role</span>
                  )}
                </td>
                <td className="py-3 px-4">
                  {user.organization_id ? (
                    <span className="text-sm text-secondary-900">
                      {getOrganizationName(user.organization_id)}
                    </span>
                  ) : (
                    <span className="text-sm text-secondary-400 italic">
                      None
                    </span>
                  )}
                </td>
                <td className="py-3 px-4">
                  <Badge
                    variant={user.is_active !== false ? 'success' : 'default'}
                  >
                    {user.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-secondary-600">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(user)}
                    className="text-primary-600 hover:text-primary-800"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit User Modal */}
      <Modal
        open={!!editingUser}
        onClose={handleCancelEdit}
        title={`Edit User: ${editingUser?.email || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              System Role
            </label>
            <select
              value={editForm.systemRole || ''}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  systemRole: e.target.value || undefined,
                })
              }
              className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">No Role</option>
              {ALL_USER_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              Organization
            </label>
            <select
              value={editForm.organization_id || ''}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  organization_id: e.target.value || undefined,
                })
              }
              className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">No Organization</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={editForm.is_active !== false}
              onChange={(e) =>
                setEditForm({ ...editForm, is_active: e.target.checked })
              }
              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <label
              htmlFor="is_active"
              className="text-sm font-medium text-secondary-700"
            >
              Active
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="ghost"
              onClick={handleCancelEdit}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveEdit}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UsersTable;
